// GA4 analytics (gtag.js) — SPA-aware, Consent Mode v2, production-only.
// Loads gtag only in production (skips localhost/preview), keeps analytics
// storage DENIED until the user accepts (consent banner), disables automatic
// page views (we send them manually on route change), and maps key API calls
// to GA4 conversion events via a single axios interceptor.
import axios from 'axios';

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const CONSENT_KEY = 'mk_analytics_consent';   // 'granted' | 'denied'
const FORCE_KEY = 'mk_ga_force';              // QA override to enable on non-prod hosts

function isDevHost() {
  const h = window.location.hostname;
  return (
    h === 'localhost' || h === '127.0.0.1' || h.endsWith('.local') ||
    h.includes('preview.emergentagent.com')
  );
}

function forceEnabled() {
  try { return localStorage.getItem(FORCE_KEY) === '1'; } catch { return false; }
}

// True only when a measurement ID exists AND we're on a production host.
export function analyticsEnabled() {
  return !!GA_ID && (!isDevHost() || forceEnabled());
}

export function getStoredConsent() {
  try { return localStorage.getItem(CONSENT_KEY); } catch { return null; }
}

function gtag() { window.dataLayer.push(arguments); }

let initialized = false;

// Load gtag, set Consent Mode v2 defaults (denied), then configure GA4.
export function initAnalytics() {
  if (initialized || !analyticsEnabled()) return;
  initialized = true;

  window.dataLayer = window.dataLayer || [];
  window.gtag = gtag;
  gtag('js', new Date());

  // Consent Mode v2 — deny everything by default until the user chooses.
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    wait_for_update: 500,
  });

  // Re-apply a previously stored consent choice immediately.
  if (getStoredConsent() === 'granted') {
    gtag('consent', 'update', { analytics_storage: 'granted' });
  }

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);

  // SPA: disable auto page_view; we fire it manually on route change.
  gtag('config', GA_ID, { send_page_view: false, anonymize_ip: true });
}

// Persist + apply the user's consent decision (analytics-only; no ads).
export function setConsent(granted) {
  try { localStorage.setItem(CONSENT_KEY, granted ? 'granted' : 'denied'); } catch { /* ignore */ }
  if (!analyticsEnabled() || !window.gtag) return;
  window.gtag('consent', 'update', {
    analytics_storage: granted ? 'granted' : 'denied',
  });
}

export function trackPageView(path, title) {
  if (!analyticsEnabled() || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: title || document.title,
  });
}

export function trackEvent(name, params = {}) {
  if (!analyticsEnabled() || !window.gtag) return;
  const clean = {};
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null) clean[k] = v; });
  window.gtag('event', name, clean);
}

export function setAnalyticsUser(userId, props = {}) {
  if (!analyticsEnabled() || !window.gtag) return;
  window.gtag('set', { user_id: userId });
  const clean = {};
  Object.entries(props).forEach(([k, v]) => { if (v !== undefined && v !== null) clean[k] = v; });
  if (Object.keys(clean).length) window.gtag('set', 'user_properties', clean);
}

// ─── Auto event mapping (single axios interceptor) ────────────────────────────
function pathOf(url) {
  try {
    const u = (url || '').startsWith('http') ? new URL(url) : new URL(url, window.location.origin);
    return u.pathname;
  } catch { return url || ''; }
}

function parseBody(data) {
  if (!data) return {};
  if (typeof data === 'string') { try { return JSON.parse(data); } catch { return {}; } }
  if (typeof data === 'object' && !(data instanceof FormData)) return data;
  return {};
}

let interceptorInstalled = false;

export function initEventTracking() {
  if (interceptorInstalled || !analyticsEnabled()) return;
  interceptorInstalled = true;
  axios.interceptors.response.use(
    (response) => { try { mapAndTrack(response); } catch { /* never break requests */ } return response; },
    (error) => Promise.reject(error),
  );
}

function mapAndTrack(response) {
  const cfg = response.config || {};
  const status = response.status;
  if (status < 200 || status >= 300) return;
  const method = (cfg.method || 'get').toLowerCase();
  const p = pathOf(cfg.url).replace(/^.*\/api/, '');   // path after /api
  const body = parseBody(cfg.data);

  if (method === 'get') {
    if (p === '/auth/me' && response.data && response.data.id) {
      setAnalyticsUser(response.data.id, {
        home_region: response.data.home_region,
        vertical: response.data.vertical,
      });
    }
    return;
  }

  if (method === 'post') {
    if (p === '/action-cards/capture/text') return trackEvent('action_card_created', { creation_method: 'text' });
    if (p === '/action-cards/capture/audio') return trackEvent('action_card_created', { creation_method: 'audio' });
    if (p === '/action-cards/manual') return trackEvent('action_card_created', { creation_method: 'manual' });
    if (/^\/drafts\/[^/]+\/process$/.test(p)) return trackEvent('action_card_created', { creation_method: 'draft' });
    if (p === '/recall/bots') return trackEvent('meeting_bot_sent');
    if (p === '/tasks') return trackEvent('task_created', { context: 'personal' });
    if (/^\/teams\/[^/]+\/tasks$/.test(p)) return trackEvent('task_created', { context: 'team' });
    if (/^\/action-cards\/[^/]+\/tasks$/.test(p)) return trackEvent('task_created', { context: 'card' });
    if (/^\/action-cards\/[^/]+\/generate-email/.test(p)) return trackEvent('email_generated');
    if (/^\/action-cards\/[^/]+\/send-email$/.test(p)) return trackEvent('email_sent');
    if (/^\/action-cards\/[^/]+\/share$/.test(p)) return trackEvent('card_shared', { access_level: body.access_level, method: 'link' });
    if (/^\/action-cards\/[^/]+\/invite$/.test(p)) return trackEvent('card_shared', { access_level: body.access_level, method: 'invite' });
    if (/^\/teams\/[^/]+\/invite$/.test(p)) return trackEvent('team_invite_sent');
    if (p === '/onboarding/complete') return trackEvent('onboarding_complete');
    return;
  }

  if (method === 'put') {
    const done = body && body.status === 'done';
    if (!done) return;
    if (/^\/tasks\/[^/]+$/.test(p)) return trackEvent('task_completed', { context: 'personal' });
    if (/^\/action-cards\/[^/]+\/tasks\/[^/]+$/.test(p)) return trackEvent('task_completed', { context: 'card' });
    if (/^\/teams\/[^/]+\/tasks\/[^/]+$/.test(p)) return trackEvent('task_completed', { context: 'team' });
  }
}
